/**
 * ADOTAR FICHAS PRESAS (fase 3E, 06/10/2026).
 *
 * O caso que ensinou: o gestor testou o app com o perfil Consulta, montou uma
 * pesagem inteira, e o banco recusou («A Consulta não grava fichas»). A ficha
 * ficou presa nos dados da Consulta, neste aparelho. Agora, quando um GESTOR
 * entra num aparelho onde outra pessoa da MESMA empresa deixou fichas que
 * o banco RECUSOU, ou que são de quem não grava (a Consulta), o app oferece:
 * «Este aparelho tem N fichas de <nome> que não chegaram ao banco. Trazer
 * para você?».
 *
 * R1A (revisão de 06/10/2026) — o que NÃO se oferece mais: a ficha de um
 * comprador que só está esperando sinal. Ela vai subir no «Sincronizar» dele;
 * trazê-la duplicava a ficha com outro número. E a ficha que JÁ ESTÁ no banco
 * nunca substitui direto a versão de lá: vai para «Conflitos», onde as
 * pesagens se juntam e só o que diverge é perguntado.
 *
 * Ao trazer:
 *  - a ficha que nunca subiu vira do gestor: número novo com a sigla dele, e
 *    sobe no «Sincronizar» dele (o banco grava `autor_id` = o gestor);
 *  - a ficha que já estava no banco e teve uma mudança recusada mantém o
 *    número e vai para «Conflitos» do gestor, que escolhe o que fica (o gestor
 *    pode mudar qualquer ficha da empresa, então a escolha sobe por ele);
 *  - os produtores recusados, e os que essas fichas citam e ainda estão na
 *    fila, vêm junto;
 *  - tudo isso SAI dos dados da outra pessoa neste aparelho (vai, não copia):
 *    se ela entrar de novo aqui, não tenta subir a mesma ficha outra vez;
 *  - o histórico da ficha diz de quem veio e o número antigo.
 *
 * SEGURANÇA — por que isto não dá poder novo a ninguém:
 *  - só o GESTOR adota (`pode(papel, 'adotarFichas')`), nunca o contrário: o
 *    comprador e a Consulta não pegam nada de ninguém;
 *  - o gestor já pode criar ficha com qualquer conteúdo e mudar qualquer
 *    ficha da empresa (001 fichas_criar e fichas_mudar). Trazer a ficha presa
 *    é o mesmo que ele digitá-la de novo, sem o trabalho — e ela sobe com o
 *    crachá DELE, pela mesma regra do banco;
 *  - só se oferecem dados de pessoas da MESMA empresa: com sinal, confirmado
 *    pela lista da equipe que o servidor devolve (só a da empresa do crachá);
 *    sem sinal, pela empresa gravada nos próprios dados (`Estado.de.contaId`).
 *    Na dúvida (dados antigos, sem sinal), não oferece;
 *  - os dados já estavam neste aparelho, abertos no navegador: a adoção não
 *    traz nada de fora dele.
 */
import { agora } from '../dominio/numeros'
import type { Ficha, Produtor } from '../dominio/tipos'
import { lerDados, lerDono, mudar, PREFIXO_DA_PESSOA, proximoNumero, type Estado } from './armazem'

export interface FichasPresas {
  /** id da pessoa dona dos dados */
  pessoaId: string
  nome: string
  chave: string
  fichas: Ficha[]
  produtores: Produtor[]
}

/**
 * As fichas PRESAS de uma pessoa: as que o servidor recusou de vez e, se os
 * dados são de quem não grava (Consulta), as que nunca subiram. A ficha do
 * comprador que só espera sinal NÃO está presa: sobe no «Sincronizar» dele.
 */
export function presasEm(e: Estado): { fichas: Ficha[]; produtores: Produtor[] } {
  const naoGrava = e.de?.papel === 'consulta'
  const fichas = e.fichas.filter((f) => f.situacao !== 'excluida'
    && (f.id in e.recusadas.fichas || (naoGrava && !f.revServidor)))
  const citados = new Set(fichas.map((f) => f.produtor.id))
  const produtores = e.produtores.filter((p) => p.id in e.recusadas.produtores
    || (citados.has(p.id) && e.pendentes.produtores.includes(p.id)))
  return { fichas, produtores }
}

/**
 * Procura neste aparelho os dados de OUTRAS pessoas com fichas presas.
 * `daEmpresa(id, contaGravada)` diz se a pessoa é da empresa de quem está
 * usando (lista da equipe com sinal; empresa gravada nos dados sem sinal).
 */
export function procurarPresas(daEmpresa: (pessoaId: string, contaGravada?: string) => boolean): FichasPresas[] {
  const eu = lerDono()
  if (!eu) return []
  const achadas: FichasPresas[] = []
  let chaves: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(PREFIXO_DA_PESSOA)) chaves.push(k)
    }
  } catch { chaves = [] }
  for (const chave of chaves) {
    const pessoaId = chave.slice(PREFIXO_DA_PESSOA.length)
    if (!pessoaId || pessoaId === eu.id) continue
    let e: Estado
    try { e = lerDados(localStorage.getItem(chave)) } catch { continue }
    if (!daEmpresa(pessoaId, e.de?.contaId)) continue
    const { fichas, produtores } = presasEm(e)
    if (!fichas.length) continue
    const nome = e.de?.nome || fichas.find((f) => f.pesadoPor)?.pesadoPor || 'outra pessoa'
    achadas.push({ pessoaId, nome, chave, fichas, produtores })
  }
  return achadas
}

/**
 * Traz as fichas presas para os dados de quem está usando (o gestor) e as
 * tira dos dados da outra pessoa. Devolve quantas fichas vieram.
 */
export function adotar(g: FichasPresas): number {
  const eu = lerDono()
  if (!eu) return 0
  let origem: Estado
  try { origem = lerDados(localStorage.getItem(g.chave)) } catch { return 0 }
  // relê na hora: só leva o que AINDA está preso (outra aba pode ter mexido)
  const { fichas, produtores } = presasEm(origem)
  if (!fichas.length) return 0
  const em = agora()

  mudar((e) => {
    for (const p of produtores) {
      const i = e.produtores.findIndex((x) => x.id === p.id)
      // marca a hora: é isso que põe o produtor na fila de quem trouxe
      const trazido = { ...p, alteradoEm: em }
      if (i < 0) e.produtores.push(trazido)
      else if (p.alteradoEm >= e.produtores[i]!.alteradoEm) e.produtores[i] = trazido
    }
    for (const f of fichas) {
      const i = e.fichas.findIndex((x) => x.id === f.id)
      // nunca subiu e já está aqui: foi trazida antes
      if (i >= 0 && !f.revServidor) continue
      const copia: Ficha = structuredClone(f)
      const antigo = copia.numero
      // nunca subiu: ganha número do gestor; já estava no banco: o número continua o do banco
      if (!copia.revServidor) copia.numero = proximoNumero(e, copia.data)
      copia.historico.push({
        em,
        oque: `Trazida dos dados de ${g.nome} neste aparelho por ${eu.nome} (não tinha chegado ao banco)`
          + (copia.numero !== antigo ? `; número ${antigo} → ${copia.numero}` : ''),
      })
      copia.alteradaEm = em
      // R1A: a que JÁ ESTÁ no banco nunca substitui direto — vai para «Conflitos»,
      // onde as pesagens das duas se juntam e o gestor escolhe só o que diverge
      if (copia.revServidor) { e.conflitos.push({ local: copia, em }); continue }
      if (i < 0) e.fichas.push(copia)
    }
  })

  // sai dos dados da outra pessoa: assim ela não tenta subir de novo a mesma ficha
  const ids = new Set(fichas.map((f) => f.id))
  const idsP = new Set(produtores.map((p) => p.id))
  origem.fichas = origem.fichas.filter((f) => !ids.has(f.id))
  origem.pendentes.fichas = origem.pendentes.fichas.filter((id) => !ids.has(id))
  origem.produtores = origem.produtores.filter((p) => !idsP.has(p.id))
  origem.pendentes.produtores = origem.pendentes.produtores.filter((id) => !idsP.has(id))
  for (const id of ids) delete origem.recusadas.fichas[id]
  for (const id of idsP) delete origem.recusadas.produtores[id]
  try { localStorage.setItem(g.chave, JSON.stringify(origem)) } catch { /* sem disco: a ficha já veio; na pior hipótese, a outra pessoa vê a cópia dela */ }
  return fichas.length
}
